import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || "https://adn-local.com";
const DEFAULT_OG = `${SITE_URL}/icon-512.png`;

/**
 * Per-page SEO + Open Graph + Twitter Card + JSON-LD injector.
 *
 * Usage:
 *   <Seo
 *      title="Local Offers · ADN Rewards"
 *      description="Hand-picked deals from Jacksonville businesses."
 *      path="/offers"
 *      schema={{ ... }}   // optional JSON-LD object or array
 *   />
 */
export default function Seo({
  title,
  description,
  path = "",
  image = DEFAULT_OG,
  schema = null,
  noindex = false,
}) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title?.includes("ADN") ? title : `${title} · ADN Rewards`;
  const schemas = !schema ? [] : Array.isArray(schema) ? schema : [schema];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="ADN Local Rewards" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

// Reusable Organization schema (mount once on the homepage)
export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "ADN Local Rewards & Loyalty Network",
  alternateName: ["ADN Rewards", "Axxess Denied Network", "ADN"],
  url: SITE_URL,
  logo: `${SITE_URL}/icon-512.png`,
  email: "hello@axxessdenied.com",
  description:
    "ADN is a community impact marketplace helping local businesses grow and community campaigns gain visibility in Jacksonville. Member-first platform with an AI shopping assistant, vetted local offers, hotel vouchers, and an ambassador referral program.",
  sameAs: [],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "general inquiries",
      email: "hello@axxessdenied.com",
      availableLanguage: ["English", "Spanish", "Portuguese", "French", "Haitian Creole"],
    },
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@adn-local.com",
      availableLanguage: ["English", "Spanish", "Portuguese", "French", "Haitian Creole"],
    },
  ],
  areaServed: {
    "@type": "City",
    name: "Jacksonville",
    address: { "@type": "PostalAddress", addressLocality: "Jacksonville", addressRegion: "FL", addressCountry: "US" },
  },
  foundingDate: "2026",
  foundingLocation: { "@type": "Place", name: "Jacksonville, FL" },
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "ADN Local Rewards",
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/offers?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function faqSchemaFromItems(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

/**
 * Schema.org payloads for the four active ADN programs. AI engines + Google
 * index each as its own first-class entity, so queries like "what is the
 * ADN Developer Certification?" or "when is the America 250 Celebration?"
 * surface direct answers rather than paragraph text.
 */
export const programSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "Event",
    "@id": `${SITE_URL}/#program-america-250`,
    name: "ADN America 250 Celebration",
    description:
      "A year-long July 4th, 2026 community photo showcase honoring Jacksonville voices, businesses, and stories that built our city.",
    startDate: "2026-07-04",
    endDate: "2027-07-04",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Jacksonville, FL",
      address: { "@type": "PostalAddress", addressLocality: "Jacksonville", addressRegion: "FL", addressCountry: "US" },
    },
    organizer: { "@type": "Organization", name: "ADN", url: SITE_URL },
    url: `${SITE_URL}/`,
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}/#program-founding-business`,
    name: "Founding Business Partners™",
    description:
      "Lifetime listing, one always-active offer, and direct member traffic for the first 100 Jacksonville businesses to claim a profile on ADN.",
    provider: { "@type": "Organization", name: "ADN", url: SITE_URL },
    areaServed: { "@type": "City", name: "Jacksonville, FL" },
    serviceType: "Local business membership and directory listing",
    url: `${SITE_URL}/partners`,
    audience: { "@type": "Audience", audienceType: "Local business owners" },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}/#program-ambassador`,
    name: "ADN Ambassador Program",
    description:
      "Members earn rewards for every friend they bring to ADN and unlock lifetime free membership at 25 successful referrals.",
    provider: { "@type": "Organization", name: "ADN", url: SITE_URL },
    serviceType: "Referral and loyalty rewards program",
    url: `${SITE_URL}/ambassador-terms`,
    audience: { "@type": "Audience", audienceType: "Active ADN members" },
  },
];
